import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Error codes Supabase Auth surfaces in the URL hash when a verification or
// recovery link is invalid. Anything in this list is routed to /verify with
// the error in a query param so VerifyPage can show a recovery UI; anything
// else is logged and the hash is wiped so the user is not stuck staring at
// a `#error=...` URL with no feedback.
const KNOWN_ERROR_CODES = new Set([
  'otp_expired',
  'access_denied',
  'invalid_token',
  'invalid_request',
  'unauthorized_client',
]);

/**
 * Reads `window.location.hash` once at mount and gracefully handles Supabase
 * Auth error returns (typically a verification link clicked after the OTP
 * TTL — 1h on this project). Success hashes (`#access_token=...`) are left
 * to supabase-js (`detectSessionInUrl: true`).
 *
 * Single-shot per provider mount via `useRef`; calling it from a Provider
 * that mounts once at app boot guarantees we don't re-trigger on every
 * route change.
 */
export function useAuthErrorHandler(): void {
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    if (typeof window === 'undefined') return;

    const hash = window.location.hash;
    if (!hash || !hash.includes('error=')) return;

    const params = new URLSearchParams(hash.slice(1));
    const errorCode = params.get('error_code');
    const error = params.get('error');
    if (!error && !errorCode) return;

    if (errorCode && KNOWN_ERROR_CODES.has(errorCode)) {
      navigate(`/verify?error=${encodeURIComponent(errorCode)}`, { replace: true });
      return;
    }

    // Unknown shape — log for triage and wipe the hash so the user isn't
    // left with the cryptic URL artefact. We do NOT navigate away in case
    // the page they landed on is otherwise a valid destination.
    console.warn('useAuthErrorHandler: unhandled auth error in URL hash', { error, errorCode });
    if (typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [navigate]);
}
